import { useMerchantCabinetConfig } from '../../config/ConfigProvider';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { groupByPredicate } from '@payler/utils';
import dayjs from 'dayjs';
import { PaginationProps } from '@payler/ui-components';
import { TCreateReportPayloadV2 } from '../../../../../api/src';
import { useCallback } from 'react';
import createLogger from 'debug';
import { useAntifraudFiltersFromUrl } from './url';
import { usePagerPropsFromUrl } from '../pager/use-pager-props-from-url';
import { usePagerUrlCallbacks } from '../pager/use-pager-url-callbacks';
import { useDropReportsCache } from '../reports/cache';
import { DEFAULT_PAGER_PROPS } from '../../const';
import { useUIDateFormat } from '../use-ui-dateFormat';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { useApi } from '../../context/ApiContextProvider';
import { captureHookError } from '../../utils/captureHookError';

const log = createLogger('hooks:payments:queries');

const ANTIFRAUD_STALE_TIME = 60 * 1000;
const ANTIFRAUD_TEMPLATES_STALE_TIME = 600 * 1000;

export const useAntifraudPaymentsQuery = () => {
  const api = useApi();
  const { culture } = useMerchantCabinetConfig();
  const contractId = useURLContractId();
  const pagerData = usePagerPropsFromUrl();
  const paymentFilters = useAntifraudFiltersFromUrl();
  const dateFormat = useUIDateFormat();
  if (!contractId) throw new Error('no contractId');
  return useQuery({
    queryKey: ['antifraud', culture, contractId, pagerData, paymentFilters],
    queryFn: async () => {
      const data = await api.getAntifraudRejectedTransactions(
        contractId,
        {
          ...paymentFilters,
          ...pagerData,
        },
        dateFormat,
      );

      const grouped = groupByPredicate(data.items, (v) => {
        const dateString = v['firstOperationDate'] as string;
        return dayjs(dateString).format('YYYY-MM-DD');
      });
      return { ...data, grouped };
    },

    staleTime: ANTIFRAUD_STALE_TIME,
  });
};

export const useAntifraudPagerProps = () => {
  const payments = useAntifraudPaymentsQuery();
  const { goToPage, setPageSize } = usePagerUrlCallbacks();
  if (payments.status !== 'success') {
    return { ...DEFAULT_PAGER_PROPS, goToPage, setPageSize } as PaginationProps;
  }
  const { page, pageSize, totalItems } = payments.data.paginationMetadata;
  return {
    goToPage,
    setPageSize,
    pageSize,
    currentPage: page,
    totalRecords: totalItems,
  } as PaginationProps;
};

export const useAntifraudTemplatesQuery = () => {
  const api = useApi();
  const contractId = useURLContractId();
  if (!contractId) throw new Error('no contractId');
  return useSuspenseQuery({
    queryKey: ['antifraud', 'templates', contractId],
    queryFn: async () => {
      const res = await api.getPaymentsTemplates(contractId);
      return res.filter((x) => x.isAntifraudDeclined === true);
    },
    staleTime: ANTIFRAUD_TEMPLATES_STALE_TIME,
  });
};

export const useSaveAntifraudPaymentsToReport = () => {
  const dropReportsCache = useDropReportsCache();
  const filters = useAntifraudFiltersFromUrl();
  const api = useApi();
  const dateFormat = useUIDateFormat();
  const contractId = useURLContractId();
  return useCallback(
    async (name: string) => {
      try {
        if (!contractId) return;
        const data: TCreateReportPayloadV2 = {
          name,
          contractIds: [contractId],
          paymentIndicators: filters.PaymentIndicators ?? [],
          paymentMethods: filters.PaymentMethods ?? [],
          currencyCodes: filters.CurrencyCodes ?? [],
          transactionStatuses: filters.TransactionStatuses ?? [],
          startDate: filters.StartDate,
          endDate: filters.EndDate,
          startAmount: filters.StartAmount,
          endAmount: filters.EndAmount,
          isAntifraudDeclined: filters.IsAntifraudDeclined,
          CardLast4: filters.CardLast4,
          Userdata: filters.Userdata,
          RRN: filters.RRN,
          direction: 'payments',
        };

        await api.createReportV2(data, dateFormat);
        dropReportsCache();
      } catch (e) {
        log('useSaveAntifraudPaymentsToReport error: %O', e);
        captureHookError(e, 'useSaveAntifraudPaymentsToReport');
      }
    },
    [dateFormat, contractId, api, dropReportsCache, filters],
  );
};
